/**
 * useMaterials.ts — coordinator hook for the Materials feature (MAT-04).
 *
 * This hook is the ONLY place where safetyMargin is converted to safetyFactor.
 * Formula: safetyFactor = 1 + safetyMargin / PERCENT_DIVISOR
 *   (PERCENT_DIVISOR = 100, imported from geometry-constants.ts — NEVER divide by 100 inline)
 *
 * Data flow:
 *   useServiceStore (selected service.materials = MaterialNorm[])
 *     + useMaterialStore (catalog = Map<materialId, Material>)
 *     + room.geometry (RoomGeometry from computed room)
 *     + safetyMargin (from useProjectStore)
 *   → buildBOM(norms, catalog, geometry, safetyFactor)
 *   → apply room.bomOverrides (user manual overrides)
 *   → computeMaterialTotals(lines)
 *   → { bomLines, materialTotals }
 *
 * FSD: feature layer — imports from entities (material, service, room) and stores.
 * Never called from within entities/.
 */

import { useMemo } from 'react';
import { useProjectStore } from '@/stores/useProjectStore';
import { useMaterialStore } from '@/stores/useMaterialStore';
import { buildBOM, computeMaterialTotals } from '@/entities/material';
import type { BOMLine, BOMTotalsResult } from '@/entities/material';
import type { Room, RoomGeometry } from '@/shared/model';
import { PERCENT_DIVISOR } from '@/shared/lib/geometry-constants';
import { roundTo } from '@/shared/lib/number';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface UseMaterialsResult {
  bomLines: BOMLine[];
  materialTotals: BOMTotalsResult;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Computes BOM lines and material totals for a given room.
 *
 * @param room      - The room with geometry and optional bomOverrides
 * @param norms     - Material norms from the selected service (MaterialNorm[])
 * @returns         - { bomLines, materialTotals }
 */
export function useMaterials(
  room: Room | null,
  norms: Array<{ materialId: string; norm: number; surfaceType?: string }>,
): UseMaterialsResult {
  const materials = useMaterialStore((s) => s.materials);
  const safetyMargin = useProjectStore((s) => s.safetyMargin);

  return useMemo(() => {
    // Empty result when room or geometry is not available
    if (!room || !norms.length) {
      const empty = computeMaterialTotals([]);
      return { bomLines: [], materialTotals: empty };
    }

    // safetyFactor conversion — ONLY place in the feature (PLAN constraint).
    // PERCENT_DIVISOR = 100 imported from geometry-constants.ts — never inline 100.
    const rawFactor = 1 + safetyMargin / PERCENT_DIVISOR;
    // CR-03: clamp to buildBOM's valid range [1, 5] so an out-of-bounds
    // safetyMargin never crashes the render. The UI layer (ProjectStats) also clamps
    // the increment button, but this guard ensures correctness from any code path.
    const safetyFactor = Math.min(5, Math.max(1, rawFactor));
    if (rawFactor !== safetyFactor) {
      console.warn(`useMaterials: safetyFactor ${rawFactor} clamped to [1, 5]`);
    }

    // Build a fast lookup catalog Map<materialId, Material>
    const catalog = new Map(materials.map((m) => [m.id, m]));

    // Resolve geometry from room.dims if computed geometry is present
    // Room geometry is computed by calculateRoomGeometry elsewhere; here we use
    // a best-effort fallback from dims for rooms without computed geometry.
    const geometry: RoomGeometry = resolveRoomGeometry(room);

    // Build raw BOM lines
    let lines = buildBOM(norms, catalog, geometry, safetyFactor);

    // Apply room.bomOverrides — user manual norm/qty overrides survive recompute.
    const overrides = room.bomOverrides || {};
    lines = lines.map((line) => {
      const override = overrides[line.materialId];
      if (!override) return line;

      let updated = { ...line };

      if (override.normIsManual) {
        // WR-01: apply the stored norm override; recompute qty/lineTotal from it
        // so the line reflects the user's custom norm, not the catalog default.
        // If norm is absent (legacy record with only normIsManual=true), keep existing values.
        if (override.norm !== undefined) {
          const manualNorm = override.norm;
          // Resolve area from geometry (same surfaceType as the line)
          // Area is already on the line from buildBOM — re-use it.
          const quantity = roundTo(manualNorm * line.area * safetyFactor, 3);
          const lineTotal = roundTo(quantity * line.pricePerUnit, 2);
          updated = { ...updated, norm: manualNorm, quantity, lineTotal, normIsManual: true };
        } else {
          // Legacy: flag only — preserve existing norm/qty
          updated = { ...updated, normIsManual: true };
        }
      }

      if (override.qty !== undefined) {
        // User typed qty directly — recompute lineTotal from manual qty
        const manualQty = override.qty;
        const lineTotal = roundTo(manualQty * line.pricePerUnit, 2);
        updated = {
          ...updated,
          quantity: manualQty,
          qtyIsManual: true,
          lineTotal,
        };
      }

      return updated;
    });

    // Non-manual lines are already fully computed by buildBOM above.
    // recomputeBOMLine is for targeted per-line refresh (e.g. single-room geometry diff),
    // not for a post-buildBOM pass — that would be a no-op and wasteful.

    const materialTotals = computeMaterialTotals(lines);

    return { bomLines: lines, materialTotals };
  }, [room, norms, materials, safetyMargin]);
}

// ---------------------------------------------------------------------------
// Geometry resolver — reads computed geometry from the room
// ---------------------------------------------------------------------------

/**
 * Resolves a RoomGeometry from a Room object.
 * The actual geometry is computed by calculateRoomGeometry in the estimate hook.
 * We read the pre-computed values if available (via room.geometry on the calculated room),
 * otherwise construct a zero-geometry fallback (safe: produces 0 quantities).
 */
function resolveRoomGeometry(room: Room): RoomGeometry {
  // The room in useProjectStore may have a `geometry` field attached by useEstimate.
  // Use structural duck-typing to read it.
  const r = room as Room & { geometry?: RoomGeometry };
  if (r.geometry) return r.geometry;

  // Fallback: read from dims (basic area estimation — geometry engine does the full calc)
  const l = Math.max(0, room.dims.length || 0) / 100; // dims are in cm
  const w = Math.max(0, room.dims.width || 0) / 100;
  const h = Math.max(0, room.dims.height || 0) / 100;
  const floorArea = l * w;
  const perimeter = 2 * (l + w);
  const wallArea = perimeter * h;

  return {
    floorArea,
    wallArea,
    perimeter,
    height: h,
    openingsArea: 0,
    partitionsArea: 0,
    windowSlopes: 0,
    doorSlopes: 0,
    partitionsEndsLinear: 0,
    nichesArea: 0,
    ledgesArea: 0,
  };
}
